import { seededRandom, shade } from '../pixel.js';
import type { PixelCanvas } from '../pixel.js';
import { box, drawBox, drawBoxes, drawCylinder, faceQuad, faces, frontFace, iso } from './iso.js';
import type { BoxPiece, FurniDrawer } from './iso.js';

/** Drawers for the bar furniture set (see protocol furniture/bar.ts). */

/** Whether a wall-facing item runs along the tile's u axis (rotations 0 and 4) or its v axis (2 and 6). */
const alongU = (rotation: number) => rotation === 0 || rotation === 4;

interface CounterTheme {
  body: string;
  overhangDark: string;
  overhangLight: string;
  /** Foot rail colour along the front. */
  accent: string;
}

const COUNTER_THEMES: Record<string, CounterTheme> = {
  wood: { body: '#3b2a22', overhangDark: '#2a1c16', overhangLight: '#4a3226', accent: '#c9a24a' },
  black: { body: '#1c1c20', overhangDark: '#121215', overhangLight: '#2a2a30', accent: '#c7cdd2' },
  silver: { body: '#8b9299', overhangDark: '#6f757c', overhangLight: '#b9c0c6', accent: '#2a2a30' },
};

function barCounterDrawer(theme: CounterTheme): FurniDrawer {
  return (pc, rotation) => {
    drawBox(pc, box(-0.46, 0.46, -0.46, 0.46, 0, 32, faces(theme.body)));
    const face = frontFace(rotation) ?? 'left';
    // Foot rail along the front.
    faceQuad(pc, face, 0.46, -0.42, 0.42, 4, 6, theme.accent);
    // A countertop overhang.
    drawBox(pc, box(-0.5, 0.5, -0.5, 0.5, 32, 36, faces(theme.overhangDark, theme.overhangLight)));
    // A shaker and a couple of glasses waiting on top.
    const shaker = iso(-0.16, -0.12, 36);
    pc.rect(shaker.x - 2, shaker.y - 8, 4, 8, '#c7cdd2');
    pc.rect(shaker.x - 1, shaker.y - 9, 2, 2, '#8b9299');
    const glass = iso(0.14, 0.1, 36);
    pc.rect(glass.x - 2, glass.y - 4, 4, 4, '#dceef2');
    pc.rect(glass.x - 2, glass.y - 4, 4, 1, '#e07b2e');
  };
}

const STOOL_SEAT_THEMES: Record<string, [string, string]> = {
  wood: ['#2a1c16', '#4a3226'],
  red: ['#6b1f1f', '#a53232'],
  black: ['#141417', '#2a2a30'],
};

function pubStoolDrawer([dark, light]: [string, string]): FurniDrawer {
  return (pc) => {
    const leg = '#8b9299';
    for (const [u, v] of [
      [-0.12, -0.12],
      [0.12, -0.12],
      [0.12, 0.12],
      [-0.12, 0.12],
    ] as const) {
      const a = iso(u, v, 22);
      const b = iso(u * 1.4, v * 1.4, 0);
      pc.line(a.x, a.y, b.x, b.y, leg);
      pc.line(a.x + 1, a.y, b.x + 1, b.y, shade(leg, 0.8));
    }
    // Foot ring, higher up than a kopitiam stool.
    const ring = iso(0, 0, 10);
    pc.rect(ring.x - 8, ring.y, 16, 1, leg);
    drawCylinder(pc, 0.2, 21, 24, dark, light);
  };
}

const BOTTLE_COLORS = ['#2f8f3a', '#a83a2c', '#c9a24a', '#3a6fd6', '#8a52c8'];

const SHELF_THEMES: Record<string, [string, string]> = {
  wood: ['#2a1c16', '#4a3226'],
  black: ['#141417', '#26262c'],
  silver: ['#6f757c', '#9aa0a6'],
};

function backBarShelfDrawer([dark, light]: [string, string]): FurniDrawer {
  return (pc, rotation) => {
    const wide = alongU(rotation);
    const frame = faces(dark, light);
    drawBox(pc, wide ? box(-0.46, 0.46, -0.14, 0.14, 0, 60, frame) : box(-0.14, 0.14, -0.46, 0.46, 0, 60, frame));
    const face = frontFace(rotation);
    if (!face) return;
    const at = 0.14;
    const rand = seededRandom(rotation + 17);
    for (const base of [10, 30, 48]) {
      faceQuad(pc, face, at, -0.42, 0.42, base - 2, base, '#3a281f');
      let s = -0.36;
      while (s < 0.34) {
        const width = 0.05 + rand() * 0.03;
        const height = 8 + Math.floor(rand() * 6);
        const color = BOTTLE_COLORS[Math.floor(rand() * BOTTLE_COLORS.length)]!;
        faceQuad(pc, face, at, s, s + width * 0.5, base, base + height, color);
        faceQuad(pc, face, at, s + width * 0.15, s + width * 0.35, base + height, base + height + 3, color);
        s += width + 0.02;
      }
    }
    // A warm glow along the bottom shelf.
    faceQuad(pc, face, at, -0.4, 0.4, 8, 10, '#ffb86c99');
  };
}

function drawCocktailTable(pc: PixelCanvas): void {
  drawCylinder(pc, 0.06, 0, 32, '#2a2a33', '#3a3a42');
  drawCylinder(pc, 0.26, 32, 36, '#efebe2', '#4a4a52');
  const rand = seededRandom(3);
  for (let i = 0; i < 10; i += 1) {
    const a = rand() * Math.PI * 2;
    const d = Math.sqrt(rand()) * 0.9;
    const p = iso(Math.cos(a) * 0.26 * d, Math.sin(a) * 0.26 * d, 36);
    pc.px(p.x, p.y, rand() > 0.5 ? '#cfc9bc' : '#dcd7cb');
  }
}

function drawNeonSign(pc: PixelCanvas, rotation: number): void {
  const wide = alongU(rotation);
  const frame = faces('#1a1a20');
  drawBoxes(pc, [wide ? box(-0.48, 0.48, -0.06, 0.06, 40, 60, frame) : box(-0.06, 0.06, -0.48, 0.48, 40, 60, frame)] as BoxPiece[]);
  const face = frontFace(rotation) ?? 'left';
  const at = 0.06;
  faceQuad(pc, face, at, -0.4, -0.06, 44, 56, '#ff4fd8');
  faceQuad(pc, face, at, 0.06, 0.4, 44, 56, '#ff4fd8');
  faceQuad(pc, face, at, -0.4, 0.4, 42, 46, '#ff9de8');
  faceQuad(pc, face, at, -0.4, 0.4, 54, 58, '#ff9de8');
}

export const BAR_DRAWERS: Record<string, FurniDrawer> = {
  bar_counter: barCounterDrawer(COUNTER_THEMES.wood!),
  bar_counter_black: barCounterDrawer(COUNTER_THEMES.black!),
  bar_counter_silver: barCounterDrawer(COUNTER_THEMES.silver!),
  pub_stool: pubStoolDrawer(STOOL_SEAT_THEMES.wood!),
  pub_stool_red: pubStoolDrawer(STOOL_SEAT_THEMES.red!),
  pub_stool_black: pubStoolDrawer(STOOL_SEAT_THEMES.black!),
  back_bar_shelf: backBarShelfDrawer(SHELF_THEMES.wood!),
  back_bar_shelf_black: backBarShelfDrawer(SHELF_THEMES.black!),
  back_bar_shelf_silver: backBarShelfDrawer(SHELF_THEMES.silver!),
  cocktail_table: drawCocktailTable,
  neon_sign: drawNeonSign,
};
